/**
 * Aero Sites — word lists and blocklists for friendly, readable site slugs.
 *
 * Slugs are the whole subdomain ({slug}.{domain}), so they must read well and be
 * easy to spell and say out loud. We generate them as `${adjective}-${noun}`
 * (e.g. swift-otter) from the two curated, child-safe lists below.
 *
 * The words are sourced from the friendly-words package (its predicates become
 * ADJECTIVES, its objects/collections become NOUNS) but are INLINED here so
 * there is NO runtime dependency on friendly-words. All entries are lowercase
 * and match /^[a-z]+$/.
 *
 * RESERVED and DENY are enforced by isValidReadableSlug in sitesUtils.js so a
 * generated slug can never collide with an operational subdomain or contain an
 * unsafe substring.
 */

export const ADJECTIVES = [
  'able', 'active', 'adept', 'agile', 'alert', 'amber', 'ample', 'amused',
  'artful', 'autumn', 'awake', 'aware', 'balmy', 'bold', 'brave', 'breezy',
  'bright', 'brisk', 'bubbly', 'busy', 'calm', 'candid', 'caring', 'charming',
  'cheery', 'chief', 'chosen', 'civic', 'clean', 'clear', 'clever', 'cloudy',
  'coastal', 'cool', 'cosmic', 'cozy', 'crafty', 'creamy', 'crimson', 'crisp',
  'cuddly', 'curious', 'daily', 'dainty', 'dandy', 'dapper', 'daring', 'deep',
  'deft', 'dewy', 'divine', 'dreamy', 'eager', 'early', 'earnest', 'easy',
  'elated', 'elder', 'electric', 'elegant', 'emerald', 'epic', 'equal', 'exact',
  'expert', 'fair', 'famous', 'fancy', 'fast', 'fearless', 'festive', 'fiery',
  'fine', 'firm', 'fleet', 'fluffy', 'fluent', 'fond', 'frank', 'free', 'fresh',
  'friendly', 'frosty', 'funny', 'gallant', 'gentle', 'giant', 'giddy', 'gifted',
  'glad', 'gleeful', 'golden', 'good', 'graceful', 'grand', 'great', 'green',
  'groovy', 'happy', 'hardy', 'hearty', 'heroic', 'hidden', 'honest', 'hopeful',
  'humble', 'icy', 'ideal', 'indigo', 'jolly', 'jovial', 'joyful', 'jubilant',
  'keen', 'kind', 'kindly', 'lacy', 'large', 'lawful', 'leafy', 'lean', 'legal',
  'light', 'lilac', 'lively', 'lone', 'loyal', 'lucid', 'lucky', 'lunar', 'lush',
  'magical', 'major', 'mature', 'melodic', 'merry', 'mighty', 'mild', 'minty',
  'misty', 'modern', 'modest', 'mossy', 'mystic', 'native', 'natural', 'neat',
  'nice', 'nimble', 'noble', 'novel', 'oaken', 'opal', 'open', 'ornate',
  'pastel', 'patient', 'peachy', 'peppy', 'perky', 'placid', 'plucky', 'polar',
  'polite', 'prime', 'pristine', 'private', 'proper', 'proud', 'pure', 'quaint',
  'quick', 'quiet', 'radiant', 'rapid', 'rare', 'ready', 'regal', 'robust',
  'rosy', 'royal', 'ruby', 'rugged', 'rustic', 'sandy', 'savvy', 'scenic',
  'serene', 'sharp', 'shiny', 'silent', 'silky', 'silver', 'simple', 'sincere',
  'sleek', 'smart', 'smooth', 'snappy', 'snowy', 'soft', 'solar', 'solid',
  'sonic', 'spry', 'stable', 'stark', 'steady', 'stellar', 'sterling', 'still',
  'stormy', 'strong', 'sunny', 'sunlit', 'super', 'supple', 'swift',
  'tame', 'tender', 'terse', 'tidy', 'timely', 'tiny', 'tranquil', 'true',
  'trusty', 'ultra', 'unique', 'upbeat', 'urban', 'valiant', 'vast', 'velvet',
  'verdant', 'vibrant', 'vital', 'vivid', 'warm', 'wavy', 'whole', 'wild',
  'willing', 'windy', 'winged', 'wise', 'witty', 'woven', 'young', 'zany',
  'zealous', 'zesty',
];

export const NOUNS = [
  'acorn', 'almond', 'anchor', 'apple', 'apricot', 'arch', 'arrow', 'atlas',
  'aurora', 'badger', 'bagel', 'bamboo', 'banana', 'basil', 'beacon', 'bean',
  'bear', 'beaver', 'bee', 'beetle', 'bell', 'berry', 'birch', 'bird', 'bison',
  'blossom', 'board', 'boat', 'bonsai', 'brook', 'bud', 'buffalo', 'bunny',
  'cabin', 'cactus', 'camel', 'candle', 'canoe', 'canyon', 'cape', 'cardinal',
  'carrot', 'castle', 'cat', 'cedar', 'cello', 'cherry', 'chestnut', 'chipmunk',
  'cliff', 'cloud', 'clover', 'coast', 'cobra', 'comet', 'condor', 'cone',
  'copper', 'coral', 'cottage', 'cougar', 'cove', 'crane', 'crater', 'cricket',
  'crow', 'crystal', 'daisy', 'deer', 'delta', 'dew', 'dingo', 'dolphin', 'dove',
  'dragon', 'dune', 'eagle', 'ember', 'emu', 'falcon', 'fawn', 'feather',
  'fennec', 'fern', 'ferry', 'finch', 'fjord', 'flamingo', 'flower', 'forest',
  'fox', 'frog', 'galaxy', 'garden', 'gecko', 'geode', 'ginger', 'glacier',
  'gnome', 'goose', 'gorilla', 'granite', 'grove', 'guava', 'harbor', 'harvest',
  'hawk', 'hazel', 'heron', 'hill', 'honey', 'horizon', 'ibis', 'iris', 'island',
  'ivory', 'jaguar', 'jasmine', 'jay', 'jewel', 'juniper', 'kangaroo', 'kayak',
  'kelp', 'kestrel', 'koala', 'lagoon', 'lake', 'lantern', 'lark', 'laurel',
  'leaf', 'lemon', 'lemur', 'lily', 'lime', 'lion', 'llama', 'lotus', 'lynx',
  'magnet', 'magpie', 'meadow', 'melon', 'meteor', 'mint', 'moose', 'moss',
  'moth', 'mountain', 'mouse', 'mulberry', 'narwhal', 'nest', 'nimbus', 'oak',
  'oasis', 'octopus', 'onyx', 'orbit', 'orca', 'orchid', 'oriole', 'otter',
  'owl', 'palm', 'panda', 'pansy', 'panther', 'parrot', 'peach', 'pear',
  'pebble', 'pelican', 'penguin', 'peony', 'pepper', 'petal', 'pier', 'pigeon',
  'pine', 'planet', 'pond', 'poppy', 'prairie', 'puffin', 'pumpkin', 'quail',
  'quartz', 'quokka', 'rabbit', 'radish', 'rain', 'rainbow', 'raven', 'reef',
  'reindeer', 'ridge', 'river', 'robin', 'rocket', 'rose', 'rowan', 'salmon',
  'sand', 'sapling', 'satellite', 'seal', 'sequoia', 'shore', 'shrimp', 'sky',
  'sloth', 'snail', 'sparrow', 'spruce', 'squid', 'star', 'starling', 'stone',
  'stork', 'stream', 'summit', 'sun', 'sunflower', 'swan', 'sycamore', 'tamarind',
  'tangerine', 'thistle', 'thrush', 'tiger', 'toucan', 'trail', 'tulip', 'tundra',
  'turtle', 'valley', 'vine', 'violet', 'vulture', 'walnut', 'walrus', 'wave',
  'willow', 'wolf', 'wombat', 'woodpecker', 'wren', 'yak', 'zebra', 'zinnia',
];

// Reserved subdomain labels that must never be assigned or allowed. Includes
// everything api/site.js already reserves (app, www, auth, api, admin, assets,
// cdn) plus our other operational / brand subdomains.
export const RESERVED = new Set([
  'app', 'www', 'auth', 'api', 'admin', 'assets', 'cdn',
  'landing', 'mail', 'smtp', 'ftp', 'ns', 'dns', 'status', 'dashboard',
  'console', 'billing', 'login', 'signup', 'account', 'support', 'help', 'docs',
  'blog', 'static', 'media', 'files', 'dev', 'staging', 'test', 'preview',
  'prod', 'secure', 'go', 'mobile', 'm', 'figmafolio', 'aero', 'aeroapp',
  // App-shell route names (App.js): a slug equal to one of these would be
  // shadowed by the SPA route on the apex, breaking the site's public URL
  // and its user-testing share link.
  'sites', 'users', 'user-testing', 'ut-test', 'ut-results', 'ut-auth', 'aero-auth',
  'plugin-auth', 'responsive', 'responsive-auth', 'iterate', 'feedback',
  'demo', 'session-review', 'folio-preview', 'folio-form', 'form', 'profile',
  'forgotpassword', 'forgot-password', 'pricing-test', 'testpage', 'privacy',
  'terms', 'privacy-policy', 'terms-and-conditions',
  // Language prefixes (i18n.js supportedLngs): /{lang} is the localized
  // landing page on figmafolio.com.
  'en', 'es', 'de', 'zh', 'jp', 'fr',
  // Astro marketing-site paths owned on the apex (middleware.js). A slug equal
  // to one of these would collide with a marketing page, so block signups from
  // claiming them. 'blog' is already reserved above.
  'pricing', 'features', 'about', 'contact', 'how-it-works', 'use-cases', 'guides',
]);

// Substrings to reject for safety (obvious profanity stems). Kept small; the
// curated word lists above are chosen so none of these ever appear in a valid
// generated slug.
export const DENY = new Set([
  'anal', 'anus', 'arse', 'ass', 'butt', 'cock', 'coon', 'crap', 'cunt', 'damn',
  'dick', 'fuck', 'hell', 'jerk', 'nazi', 'piss', 'porn', 'prick', 'rape',
  'scum', 'sex', 'shit', 'slut', 'suck', 'tit', 'turd', 'twat', 'wank', 'whore',
]);
